import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, Volume2, VolumeX, Wifi, FileText } from "lucide-react";
import { DESKTOP_ICONS, appMeta, renderAppContent } from "@/components/os/apps";
import { IDENTITY } from "@/data/content";
import { isSoundEnabled, onSoundChange, setSoundEnabled, playBeep } from "@/lib/sound";

const useClock = () => {
    const [time, setTime] = useState("--:--");
    useEffect(() => {
        const fmt = new Intl.DateTimeFormat("en-GB", {
            timeZone: "Asia/Jakarta",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
        const tick = () => setTime(fmt.format(new Date()));
        tick();
        const id = setInterval(tick, 10000);
        return () => clearInterval(id);
    }, []);
    return time;
};

export default function MobileShell() {
    const [stack, setStack] = useState([]);
    const [soundOn, setSoundOn] = useState(isSoundEnabled());
    const time = useClock();
    useEffect(() => onSoundChange(setSoundOn), []);

    const current = stack[stack.length - 1];
    const open = (key) => {
        playBeep(760, 0.05, 0.045, "square");
        setStack((s) => [...s, key]);
    };
    const back = () => {
        playBeep(500, 0.04, 0.04, "square");
        setStack((s) => s.slice(0, -1));
    };
    const toggleSound = () => {
        const next = !soundOn;
        setSoundEnabled(next);
        if (next) setTimeout(() => playBeep(880, 0.09, 0.07, "square"), 30);
    };

    const meta = current ? appMeta(current) : null;

    return (
        <div className="h-[100dvh] flex flex-col" data-testid="mobile-shell">
            <div className="h-9 shrink-0 flex items-center justify-between px-4 text-neutral-300 bg-[#0c0d10]/90 border-b border-white/10 select-none">
                <span className="flex items-center gap-2 font-bold text-[11px] text-white">
                    <img src="/logo.png" alt="" className="w-4 h-4 rounded" />
                    wikiOS mobile
                </span>
                <span className="flex items-center gap-3 font-mono text-[10px] text-neutral-400">
                    <Wifi size={11} />
                    <button
                        data-testid="sound-toggle-button"
                        onClick={toggleSound}
                        aria-label={soundOn ? "Mute sound effects" : "Unmute sound effects"}
                    >
                        {soundOn ? <Volume2 size={12} /> : <VolumeX size={12} />}
                    </button>
                    <span data-testid="mobile-clock">{time} WIB</span>
                </span>
            </div>

            <div className="flex-1 relative overflow-hidden">
                <div className="absolute inset-0 p-6 flex flex-col">
                    <div className="grid grid-cols-3 gap-x-4 gap-y-6 pt-4">
                        {DESKTOP_ICONS.map((d) => (
                            <button
                                key={d.key}
                                data-testid={d.testid}
                                onClick={() => open(d.key)}
                                className="group flex flex-col items-center gap-2 outline-none"
                            >
                                <span
                                    className={`w-14 h-14 rounded-2xl flex items-center justify-center border transition-transform duration-150 group-active:scale-90 ${
                                        d.accent
                                            ? "bg-vermilion border-vermilion text-white shadow-[0_10px_28px_rgba(230,59,46,0.4)]"
                                            : "bg-[#23262d] border-white/15 text-neutral-200 shadow-[0_10px_28px_rgba(0,0,0,0.45)]"
                                    }`}
                                >
                                    <d.icon size={24} strokeWidth={1.8} />
                                </span>
                                <span className="text-[11px] font-medium text-neutral-200 [text-shadow:0_1px_3px_rgba(0,0,0,0.8)]">
                                    {d.label}
                                </span>
                            </button>
                        ))}
                        <a
                            href={IDENTITY.resume}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="mobile-icon-resume"
                            className="group flex flex-col items-center gap-2"
                        >
                            <span className="w-14 h-14 rounded-2xl flex items-center justify-center bg-[#23262d] border border-white/15 text-neutral-200 shadow-[0_10px_28px_rgba(0,0,0,0.45)] transition-transform duration-150 group-active:scale-90">
                                <FileText size={24} strokeWidth={1.8} />
                            </span>
                            <span className="text-[11px] font-medium text-neutral-200 [text-shadow:0_1px_3px_rgba(0,0,0,0.8)]">Resume.pdf</span>
                        </a>
                    </div>
                    <div className="mt-auto text-center font-mono text-[9px] uppercase tracking-[0.25em] text-white/25 select-none">
                        wikiOS 26.04 · mobile shell
                    </div>
                </div>

                <AnimatePresence>
                    {current && (
                        <motion.div
                            key={stack.join(">")}
                            data-testid="mobile-app-view"
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "100%" }}
                            transition={{ type: "spring", stiffness: 320, damping: 34 }}
                            className="absolute inset-0 flex flex-col bg-[#f6f5f4] text-[#1a1a1a]"
                        >
                            <div className="h-11 shrink-0 flex items-center gap-2 px-3 bg-[#2b2e34] text-neutral-200 select-none">
                                <button
                                    data-testid="mobile-back-button"
                                    onClick={back}
                                    aria-label="Back"
                                    className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-white/10 active:bg-white/15"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                {meta?.icon && <meta.icon size={13} className="text-neutral-400" />}
                                <span className="text-xs font-medium truncate">{meta?.title}</span>
                            </div>
                            <div className="flex-1 overflow-y-auto overscroll-contain">
                                {renderAppContent(current, (id) => open(`project:${id}`))}
                            </div>
                            <button
                                data-testid="mobile-home-bar"
                                onClick={back}
                                aria-label="Home"
                                className="shrink-0 h-7 bg-[#0c0d10] flex items-center justify-center"
                            >
                                <span className="w-28 h-1 rounded-full bg-white/40" />
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}
